import React from 'react';
import { cssTransition, toast } from 'react-toastify';

import { Button } from 'components/ui';
import IconAlert from 'components/ui/icons/IconAlert';
import IconCheck from 'components/ui/icons/IconCheck';
import IconInfoCircle from 'components/ui/icons/IconInfoCircle';
import IconX from 'components/ui/icons/IconX';
import ToastBody from 'components/ui/ToastBody';
import { cx } from 'helpers/StylingHelpers';
import { generateUUID } from 'helpers/utils';

import type { ToastOptions as DefaultOptions, UpdateOptions } from 'react-toastify';
import type { IconComponent, ValueOf } from 'types';
import { isJSXElement, isReactNode } from 'types';

// Types

export interface ToastOptions extends DefaultOptions {
  autoUpdate?: boolean,
  //
  withProgressBar?: boolean,
  withIcon?: boolean,
  withCloseButton?: boolean,
  //
  closeButtonClassName?: string,
  //
  onDismiss?: () => void
}

export interface ToastUpdateOptions extends UpdateOptions {
  withProgressBar?: boolean,
  withIcon?: boolean,
  withCloseButton?: boolean,
  //
  closeButtonClassName?: string,
  //
  onDismiss?: () => void
}

export type ToastId = string | number;
export type ToastMessage = string | JSX.Element | React.ReactNode;
export type ToastData = ToastMessage | (() => ToastMessage) | {
  title?: string,
  message?: string
  | JSX.Element
  | React.ReactNode,
  icon?: IconComponent
};

// Methods

const showSuccessToast = (data: ToastData, options?: ToastOptions) => {
  return showToast(data, TYPES.SUCCESS, options);
};

const showErrorToast = (data: ToastData, options?: ToastOptions) => {
  return showToast(data, TYPES.ERROR, options);
};

const showWarnToast = (data: ToastData, options?: ToastOptions) => {
  return showToast(data, TYPES.WARNING, options);
};

const showInfoToast = (data: ToastData, options?: ToastOptions) => {
  return showToast(data, TYPES.INFO, options);
};

const showDefaultToast = (data: ToastData, options?: ToastOptions) => {
  return showToast(data, TYPES.DEFAULT, options);
};

// Helpers

const showToast = (data: ToastData, type: ValueOf<typeof TYPES>, options?: ToastOptions) => {
  const {
    //
    containerId = TOAST_CONTAINER_IDS.GENERIC,
    toastId: baseToastId,
    //
    closeButtonClassName,
    //
    theme = 'light',
    position = TOAST_POSITIONS.BottomRight,
    //
    autoUpdate = true,
    autoClose = 3000,
    //
    withIcon = true,
    withCloseButton = true,
    withProgressBar = true,
    //
    onDismiss,
    //
    ...rest
    //
  } = options || {};

  // Toast ID

  const toastId = baseToastId || generateUUID();

  // Method

  const method = type === 'default'
    ? undefined
    : TYPES_TO_METHOD[type];

  // Message

  let message: React.ReactNode;
  let icon: React.JSX.Element | undefined;

  if (typeof data === 'string' || isJSXElement(data) || isReactNode(data)) {
    message = data;
  } else if (typeof data === 'function') {
    message = data();
  } else if (data.title) {
    message = (
      <ToastBody
        title={data.title}
        message={data.message || ''}
      />
    );
  } else {
    message = data?.message;
  }

  // Icon

  const TypeIcon = TYPES_TO_ICON[type];

  if (withIcon) {
    if (data && typeof data === 'object' && 'icon' in data && data.icon) {
      icon = <data.icon size={20} strokeWidth={2.5} />;
    } else if (TypeIcon) {
      icon = <TypeIcon size={20} strokeWidth={2.5} />;
    } else {
      icon = undefined;
    }
  }

  // Props

  const toastProps: ToastOptions = {
    containerId,
    toastId,
    //
    autoClose,
    //
    position,
    theme,
    icon,
    hideProgressBar: !withProgressBar,
    //
    closeButton: !withCloseButton
      ? undefined
      : ({ closeToast }) => (
        <Button
          className={cx(
            closeButtonClassName
          )}
          icon={IconX}
          iconProps={{
            size: 14,
            strokeWidth: 2,
          }}
          size="medium"
          style={
            type !== 'default'
              ? {}
              : {}
          }
          onClick={(e) => {
            closeToast(e);
            if (onDismiss) {
              onDismiss();
            }
          }}
        />
      ),
    ...rest
  };

  // Render

  const render = (
    <div className={cx('Toastify__toast__message')}>
      {message}
    </div>
  );

  // Auto Update

  if (autoUpdate && isToastActive(toastId)) {
    updateToast(toastId, {
      ...toastProps,
      render
    });
    return toastId;
  }

  // Show Toast

  if (method) {
    toast[method](
      render,
      toastProps
    );
  } else {
    toast(render, toastProps);
  }

  // Return

  return toastId;
};

const dismissToast = (id: ToastId) => toast.dismiss(id);

const updateToast = (id: ToastId, options: ToastUpdateOptions = {}) => {
  return toast.update(id, {
    ...options
  });
};

const isToastActive = (id: ToastId) => {
  const { isActive } = toast;
  return isActive(id);
};

// Animations

const fadeTransition = cssTransition({
  enter: 'toast-transition-enter-fade-in-bottom',
  exit: 'toast-transition-exit-fade-out',
});

const slideFadeTransition = cssTransition({
  enter: 'toast-transition-enter-slide-right',
  exit: 'toast-transition-exit-fade-out'
});

const popFadeTransition = cssTransition({
  enter: 'toast-transition-enter-pop-in',
  exit: 'toast-transition-exit-fade-out'
});

// Constants

export const TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  DEFAULT: 'default'
} as const;

export type TYPE = ValueOf<typeof TYPES>;

const TYPES_TO_METHOD = {
  [TYPES.SUCCESS]: 'success',
  [TYPES.ERROR]: 'error',
  [TYPES.WARNING]: 'warn',
  [TYPES.INFO]: 'info',
} as const;

// Icons

const TYPES_TO_ICON: Partial<Record<TYPE, IconComponent>> = {
  [TYPES.SUCCESS]: IconCheck,
  [TYPES.INFO]: IconInfoCircle,
  [TYPES.WARNING]: IconAlert,
  [TYPES.ERROR]: IconAlert,
};

// Exports

export const TOAST_CONTAINER_IDS = {
  GENERIC: 'genericToastContainerId',
  CUSTOM: 'customToastContainerId',
  BANNERS: 'bannersToastContainerId',
} as const;

export const TOAST_POSITIONS = {
  TopLeft: 'top-left',
  TopCenter: 'top-center',
  TopRight: 'top-right',
  BottomLeft: 'bottom-left',
  BottomCenter: 'bottom-center',
  BottomRight: 'bottom-right'
} as const;

export type TOAST_POSITION = ValueOf<typeof TOAST_POSITIONS>;

// Export

export {
  //
  showSuccessToast,
  showErrorToast,
  showWarnToast,
  showInfoToast,
  showDefaultToast,
  showToast,
  //
  isToastActive,
  dismissToast,
  updateToast,
  //
  slideFadeTransition,
  popFadeTransition,
  fadeTransition,
  //
};
